/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useInitialProjectTemplate } from '@jira/platform__services-project-properties/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { NAVIGATION_ITEM, BUTTON } from '../../constants/analytics/action-subject-type.tsx';
import {
	FIXED_ITEM,
	FIXED_ITEM_BUTTON,
	CUSTOM_ITEM,
} from '../../constants/analytics/component-type.tsx';
import {
	MEATBALL_DROPDOWN,
	MORE_PROJECTS,
	PROJECTS,
	VIEW_ALL_PROJECTS,
} from '../../constants/analytics/item-id.tsx';
import { PROJECT } from '../../constants/analytics/item-type.tsx';
import { LEVEL_ONE } from '../../constants/analytics/level.tsx';
import { GLOBAL } from '../../constants/analytics/navigation-layer.tsx';
import type { ProjectParams } from '../types.tsx';
import { getSectionByStarredOrRecent } from '../utils.tsx';

export const useProjectsAnalyticsL1 = (): {
	sendViewAllProjectsAnalyticsEvent: () => void;
	sendMoreProjectsAnalyticsEvent: () => void;
	sendMeatBallDropDownAnalyticsEvent: () => void;
	sendCreateProjectAnalyticsEvent: () => void;
	sendProjectMenuItemAnalyticsEvent: ({
		starred,
		recent,
		projectId,
		projectType,
	}: ProjectParams) => void;
	sendProjectMenuContentAnalyticsEvent: () => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialProjectTemplate = useInitialProjectTemplate();

	// `initialProjectTemplate` resolves AFTER hydration (undefined -> value) from
	// a client-side project-property fetch. Including it directly in
	// `commonAttributes` changes that object's identity once it loads, which
	// invalidates every analytics `useCallback` below and cascades unnecessary
	// re-renders into the sidebar (measured via Chippy). The template is only
	// read at event-FIRE time (all events fire on user interaction, after load),
	// so we read it lazily via a ref to keep `commonAttributes` identity stable.
	// Gated behind `navx-4728-jira-stability-fixes`.
	const initialProjectTemplateRef = useRef(initialProjectTemplate);

	// This runs for both sides of the gate (intentional)
	useEffect(() => {
		initialProjectTemplateRef.current = initialProjectTemplate;
	}, [initialProjectTemplate]);

	const stableCommonAttributes = useMemo(
		() => ({
			level: LEVEL_ONE,
			navigationLayer: GLOBAL,
			// Getter reads the latest value at fire time, keeping object identity stable.
			get initialProjectTemplate() {
				return initialProjectTemplateRef.current;
			},
		}),
		// Intentionally empty: identity must remain stable; the template is read
		// lazily through the ref above.
		[],
	);

	const unstableCommonAttributes = useMemo(
		() => ({
			level: LEVEL_ONE,
			navigationLayer: GLOBAL,
			initialProjectTemplate,
		}),
		[initialProjectTemplate],
	);

	const commonAttributes = fg('navx-4728-jira-stability-fixes')
		? stableCommonAttributes
		: unstableCommonAttributes;

	// fixed item - view all projects link
	const sendViewAllProjectsAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM,
			itemId: VIEW_ALL_PROJECTS,
		};
		fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// fixed item - more projects flyout trigger
	const sendMoreProjectsAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM,
			parentItemId: PROJECTS,
			itemId: MORE_PROJECTS,
		};
		fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// fixed item - meatball icon button on projects menu (top level)
	const sendMeatBallDropDownAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: BUTTON,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM_BUTTON,
			itemId: MEATBALL_DROPDOWN,
		};
		fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// fixed item - create project (+) button on projects menu (top level)
	const sendCreateProjectAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: BUTTON,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM_BUTTON,
			itemId: 'createProject',
		};
		fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// custom item - project menu item that displays project name (can be expandable menu or a link)
	const sendProjectMenuItemAnalyticsEvent = useCallback(
		({ starred, recent, projectId, projectType }: ProjectParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM,
				itemType: PROJECT,
				itemId: projectId,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
			fireUIAnalytics(event, 'projectsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	const sendProjectMenuContentAnalyticsEvent = useCallback((): void => {
		const attributes = {
			...commonAttributes,
			componentType: CUSTOM_ITEM,
			itemId: 'projectsNavigationMenuItemContent',
		};

		fireUIAnalytics(
			createAnalyticsEvent({}),
			'projectsNavigationMenuItemContent rendered',
			attributes,
		);
	}, [commonAttributes, createAnalyticsEvent]);

	return {
		sendViewAllProjectsAnalyticsEvent,
		sendMoreProjectsAnalyticsEvent,
		sendMeatBallDropDownAnalyticsEvent,
		sendCreateProjectAnalyticsEvent,
		sendProjectMenuItemAnalyticsEvent,
		sendProjectMenuContentAnalyticsEvent,
	};
};
